import { useState, useEffect } from "react";
import { functions } from "../firebase.js";
import { httpsCallable } from "firebase/functions";
import { C } from "../utils/palette.js";

const KATEGORIE = ["UI", "Dane", "Logowanie", "Inne"];
const PRIORYTETY = [
  { value: "niski",   label: "Niski",   color: "#6b7280" },
  { value: "sredni",  label: "Średni",  color: "#f59e0b" },
  { value: "pilne",   label: "Pilne",   color: "#ef4444" },
];

// Tooltip znika po 3 sesjach
const LS_KEY = "fl_bug_seen";
function getSeenCount() { return parseInt(localStorage.getItem(LS_KEY) || "0", 10); }
function bumpSeenCount() { localStorage.setItem(LS_KEY, String(getSeenCount() + 1)); }

export default function BugReporter({ userType, userId }) {
  const [open, setOpen]           = useState(false);
  const [opis, setOpis]           = useState("");
  const [kategoria, setKategoria] = useState("UI");
  const [priorytet, setPriorytet] = useState("sredni");
  const [status, setStatus]       = useState("idle"); // idle | sending | ok | err
  const [showTip, setShowTip]     = useState(false);

  useEffect(() => {
    const seen = getSeenCount();
    if (seen < 3) {
      setShowTip(true);
      bumpSeenCount();
      // Tooltip znika po 6 sekundach
      const t = setTimeout(() => setShowTip(false), 6000);
      return () => clearTimeout(t);
    }
  }, []);

  function reset() {
    setOpis(""); setKategoria("UI"); setPriorytet("sredni"); setStatus("idle");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!opis.trim()) return;
    setStatus("sending");
    try {
      const fn = httpsCallable(functions, "reportBug");
      await fn({
        opis: opis.trim(),
        kategoria,
        priorytet,
        ekran: window.location.hash || window.location.pathname || "/",
        userType: userType || "nieznany",
        userId: userId || "—",
      });
      setStatus("ok");
      setTimeout(() => { setOpen(false); reset(); }, 2000);
    } catch {
      setStatus("err");
    }
  }

  return (
    <>
      {/* ── Floating button + tooltip ── */}
      <div style={{ position: "fixed", bottom: 80, right: 16, zIndex: 9000, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
        {/* Tooltip — pierwsze 3 sesje */}
        {showTip && (
          <div style={{
            background: "#1f2937",
            border: "1px solid #374151",
            borderRadius: 8,
            padding: "6px 10px",
            fontSize: 12,
            color: "#d1d5db",
            whiteSpace: "nowrap",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            animation: "fl-fade-in .3s ease",
          }}>
            Znalazłeś błąd? Kliknij 🐞
          </div>
        )}
        <button
          onClick={() => { setOpen(true); setShowTip(false); reset(); }}
          title="Zgłoś błąd"
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "#1f2937",
            border: showTip ? "1.5px solid #60a5fa" : "1.5px solid #374151",
            boxShadow: showTip ? "0 0 0 4px rgba(96,165,250,0.2)" : "0 2px 8px rgba(0,0,0,0.35)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            padding: 0,
            opacity: 0.75,
            transition: "opacity .2s, box-shadow .3s, border-color .3s",
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = 1}
          onMouseLeave={e => e.currentTarget.style.opacity = 0.75}
        >
          🐞
        </button>
      </div>

      {/* ── Modal overlay ── */}
      {open && (
        <div
          onClick={e => { if (e.target === e.currentTarget) { setOpen(false); reset(); } }}
          style={{
            position: "fixed", inset: 0, zIndex: 9001,
            background: "rgba(0,0,0,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 16,
          }}
        >
          <div style={{
            background: "#111827",
            border: "1px solid #374151",
            borderRadius: 16,
            padding: "20px 20px 16px",
            width: "100%",
            maxWidth: 380,
            boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
          }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <span style={{ color: "#f9fafb", fontWeight: 700, fontSize: 15 }}>🐞 Zgłoś błąd</span>
              <button
                onClick={() => { setOpen(false); reset(); }}
                style={{ background: "none", border: "none", color: "#9ca3af", fontSize: 20, cursor: "pointer", lineHeight: 1, padding: 0 }}
              >×</button>
            </div>

            {status === "ok" ? (
              <div style={{ textAlign: "center", padding: "24px 0", color: C.green || "#22c55e" }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>✓</div>
                <div style={{ color: "#f9fafb", fontWeight: 600 }}>Zgłoszono, dzięki!</div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {/* Opis */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: "block", color: "#9ca3af", fontSize: 12, marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>
                    Co się stało?
                  </label>
                  <textarea
                    value={opis}
                    onChange={e => setOpis(e.target.value)}
                    placeholder="Opisz błąd..."
                    rows={3}
                    required
                    style={{
                      width: "100%", boxSizing: "border-box",
                      background: "#1f2937", border: "1px solid #374151",
                      borderRadius: 8, color: "#f9fafb", fontSize: 14,
                      padding: "8px 10px", resize: "vertical", fontFamily: "inherit",
                      outline: "none",
                    }}
                  />
                </div>

                {/* Kategoria */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: "block", color: "#9ca3af", fontSize: 12, marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>
                    Kategoria
                  </label>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {KATEGORIE.map(k => (
                      <button
                        key={k} type="button"
                        onClick={() => setKategoria(k)}
                        style={{
                          padding: "5px 12px", borderRadius: 20, fontSize: 13, cursor: "pointer",
                          border: `1.5px solid ${kategoria === k ? "#60a5fa" : "#374151"}`,
                          background: kategoria === k ? "rgba(96,165,250,0.15)" : "transparent",
                          color: kategoria === k ? "#60a5fa" : "#9ca3af",
                          transition: "all .15s",
                        }}
                      >{k}</button>
                    ))}
                  </div>
                </div>

                {/* Priorytet */}
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: "block", color: "#9ca3af", fontSize: 12, marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>
                    Priorytet
                  </label>
                  <div style={{ display: "flex", gap: 6 }}>
                    {PRIORYTETY.map(p => (
                      <button
                        key={p.value} type="button"
                        onClick={() => setPriorytet(p.value)}
                        style={{
                          flex: 1, padding: "5px 0", borderRadius: 8, fontSize: 13, cursor: "pointer",
                          border: `1.5px solid ${priorytet === p.value ? p.color : "#374151"}`,
                          background: priorytet === p.value ? `${p.color}22` : "transparent",
                          color: priorytet === p.value ? p.color : "#6b7280",
                          transition: "all .15s",
                        }}
                      >{p.label}</button>
                    ))}
                  </div>
                </div>

                {/* Submit */}
                {status === "err" && (
                  <div style={{ color: "#ef4444", fontSize: 12, marginBottom: 8, textAlign: "center" }}>
                    Błąd wysyłania — spróbuj ponownie
                  </div>
                )}
                <button
                  type="submit"
                  disabled={status === "sending" || !opis.trim()}
                  style={{
                    width: "100%", padding: "10px 0", borderRadius: 10,
                    background: status === "sending" ? "#374151" : "#2563eb",
                    color: "#fff", fontWeight: 600, fontSize: 14,
                    border: "none", cursor: status === "sending" ? "not-allowed" : "pointer",
                    transition: "background .15s",
                  }}
                >
                  {status === "sending" ? "Wysyłanie..." : "Wyślij zgłoszenie"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
